import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";

/**
 * Words shown inside the illustrations. They reuse existing translations so the
 * art adds no new copy; tickers, prices and TP/SL notation stay as they are.
 */
const useArtLabels = () => {
  const { t: onboarding } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const { t: bots } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { t: common } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { t: portfolio } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const { t: dashboard } = useTypedTranslation(LanguageNameSpaces.DASHBOARD);
  const { t: trades } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const { t: settings } = useTypedTranslation(LanguageNameSpaces.SETTINGS);
  return {
    long: bots("labels.long"),
    short: bots("labels.short"),
    buy: portfolio("buttons.buy"),
    sell: portfolio("positions.sell"),
    size: portfolio("positions.size"),
    market: portfolio("create_order.order_type.market"),
    profit: bots("labels.profit"),
    grids: bots("labels.grids"),
    trades: bots("tabs.trades"),
    takeProfit: bots("labels.take_profit"),
    priceRange: bots("labels.price_range"),
    amount: bots("labels.amount"),
    entry: trades("orders.entry_title"),
    closed: trades("tabs.closed"),
    balance: dashboard("balance.title"),
    pnl: dashboard("open_trades.pnl_label"),
    active: common("filters.status.common.active"),
    following: common("filters.status.follow.following"),
    demo: common("demo_mode.workspace_demo_short"),
    live: common("demo_mode.workspace_live_short"),
    signalsBot: common("create_bot_modal.title.signal"),
    gridBot: common("create_bot_modal.title.grid"),
    dcaBot: common("create_bot_modal.title.dca"),
    tradingViewBot: common("create_bot_modal.title.trading_view"),
    filled: onboarding("revision.order_filled"),
    received: onboarding("revision.received"),
    signalReceived: onboarding("revision.signals_action1"),
    entryAlert: onboarding("revision.entry_alert"),
    entryFilled: onboarding("revision.entry_filled"),
    sellFilled: onboarding("revision.sell_filled"),
    positionClosed: onboarding("revision.position_closed"),
    averageEntry: onboarding("revision.average_entry"),
    stopLoss: onboarding("revision.order_stop_label"),
    entryN: (number: number) =>
      onboarding("revision.order_entry_label", { number }),
    targetN: (number: number) =>
      onboarding("revision.order_target_label", { number }),
    buyOrder: onboarding("revision.order_buy_label"),
    sellOrder: onboarding("revision.order_sell_label"),
    pending: onboarding("revision.order_pending"),
    orderActive: onboarding("revision.order_active"),
    buyFilled: onboarding("revision.buy_filled"),
    buyFilledAgain: onboarding("revision.grid_buy_again"),
    firstBuyFilled: onboarding("revision.signals_action2"),
    secondBuyFilled: onboarding("revision.signals_action3"),
    thirdBuyFilled: onboarding("revision.third_buy_filled"),
    takeProfitFilled: onboarding("revision.signals_action4"),
    sampleSetup: onboarding("revision.sample_setup"),
    eventCount: (current: number, total: number) =>
      onboarding("revision.event_count", { current, total }),
    hedgingBot: common("create_bot_modal.title.hedging"),
    apiKey: portfolio("create_account.api_key"),
    apiSecret: portfolio("create_account.api_secret"),
    quickConnect: portfolio("create_account.tabs.quick_connect"),
    connected: settings("external_accounts.connected"),
    accumulatedProfit: bots("statistics.accumulated_profit"),
    profitByDay: bots("statistics.profit_by_day"),
    virtualOnly: onboarding("guided.virtual_only"),
    practiceReady: onboarding("guided.practice_ready"),
    virtualFunds: onboarding("guided.benefit_account"),
  };
};

export type ArtLabels = ReturnType<typeof useArtLabels>;

export default useArtLabels;
